import { WatchQueryFetchPolicy, useLazyQuery, useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';

interface UseListUsersInput {
  input: API.ListUsers;
  customCacheKey?: string;
  skip?: boolean;
  // Keep showing the previous result while a new variable set loads, instead of
  // dropping to an empty list. Useful for type-to-filter UIs where each query
  // string is a distinct cache entry (e.g. @mention search) so the list narrows
  // in place rather than blanking on every keystroke.
  keepPreviousData?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface UseListUsersResult {
  users: NonNullable<NonNullable<API.ListUsersQuery['listUsers']>['users']>[number][];
  total: number;
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  loadMore: () => void;
}

interface UseListUsersLazyResult {
  users: NonNullable<NonNullable<API.ListUsersQuery['listUsers']>['users']>[number][];
  totalCount: number;
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  loadMore: () => void;
  fetch: () => void;
}

const inputDefaults: API.ListUsers = {
  excludeSystemUsers: true,
  orderBys: [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
};

export function useListUsers({
  input,
  customCacheKey,
  skip,
  keepPreviousData,
  fetchPolicy,
}: UseListUsersInput): UseListUsersResult {
  const queryInput = useMemo(() => {
    return {
      ...inputDefaults,
      ...input,
      name: getNameInput(input.name),
    };
  }, [input]);

  const {
    data: incomingData,
    previousData,
    loading,
    error,
    fetchMore,
  } = useQuery<API.ListUsersQuery>(Queries.user.listUsers, {
    variables: {
      input: queryInput,
      customCacheKey: customCacheKey ?? '',
    },
    skip,
    fetchPolicy,
  });

  // If keepPreviousData is set, fall back to the last resolved result while the
  // new data is loading.
  const fallBackData = keepPreviousData ? previousData : undefined;
  const data = incomingData ?? fallBackData;

  const loadMore = useCallback(() => {
    if (!isNil(incomingData?.listUsers?.users) && incomingData?.listUsers?.hasMore) {
      const offset = incomingData.listUsers.users.length;
      fetchMore({
        variables: {
          input: { ...queryInput, offset },
          customCacheKey: customCacheKey ?? '',
        },
      });
    }
  }, [incomingData?.listUsers, fetchMore, queryInput, customCacheKey]);

  return useMemo(
    () => ({
      users: data?.listUsers?.users ?? [],
      total: data?.listUsers?.total ?? 0,
      hasMore: data?.listUsers?.hasMore ?? false,
      loadMore,
      loading,
      error,
    }),
    [data?.listUsers, loadMore, loading, error],
  );
}

export function useListUsersById(
  ids: number[],
  orderBys?: API.ListUsers['orderBys'],
): UseListUsersResult {
  const input = {
    ids,
    includeDeactivated: true,
    excludeSystemUsers: true,
    orderBys: orderBys ?? [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
  };
  const skip = isEmpty(ids);
  return useListUsers({ input, skip });
}

export function useListUsersByName(
  name: string,
  orderBys?: API.ListUsers['orderBys'],
): UseListUsersResult {
  return useListUsers({
    input: {
      name: name.trim(),
      orderBys: orderBys ?? [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
    },
  });
}

export function useUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return useMemo(() => getUsersWithoutMe(users), [users]);
}

export function getUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return users.filter(user => !user.isMe);
}

export function useSortedUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return useMemo(() => getSortedUsersWithoutMe(users), [users]);
}

export function getSortedUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return getUsersWithoutMe(users).sort((a, b) => a.fullName.localeCompare(b.fullName));
}

export function useListUsersLazy({
  input,
  customCacheKey,
}: Omit<UseListUsersInput, 'skip'>): UseListUsersLazyResult {
  const queryInput = { ...inputDefaults, ...input };
  const [fetch, { data, loading, error, fetchMore }] = useLazyQuery<API.ListUsersQuery>(
    Queries.user.listUsers,
    {
      variables: {
        input: queryInput,
        customCacheKey: customCacheKey ?? '',
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const {
    ids,
    name,
    username,
    limit,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  } = queryInput;
  const loadMore = useCallback(() => {
    if (!isNil(data?.listUsers?.users) && data?.listUsers?.hasMore) {
      const offset = data.listUsers.users.length;
      fetchMore({
        variables: {
          input: {
            ids,
            name,
            username,
            limit,
            offset,
            excludeSystemUsers,
            isActive,
            isDeactivated,
            orderBys,
            memberOfChannelIds,
            notMemberOfChannelIds,
          },
          customCacheKey: customCacheKey ?? '',
        },
      });
    }
  }, [
    data?.listUsers,
    fetchMore,
    ids,
    limit,
    name,
    username,
    customCacheKey,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  ]);

  return useMemo(
    () => ({
      users: data?.listUsers?.users ?? [],
      hasMore: data?.listUsers?.hasMore ?? false,
      totalCount: data?.listUsers?.total ?? 0,
      loadMore,
      loading,
      error,
      fetch,
    }),
    [
      data?.listUsers?.users,
      loadMore,
      loading,
      error,
      data?.listUsers?.hasMore,
      fetch,
      data?.listUsers?.total,
    ],
  );
}

function getNameInput(query?: string | null): string | undefined {
  const trimmed = query?.trim();
  if (isEmpty(trimmed)) {
    return undefined;
  } else {
    return trimmed;
  }
}
